import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import {z} from 'zod';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
  getUsersMe,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {CommunityQuestionListPage} from '../../features/community/components/CommunityQuestionListPage';
import {CommunityLayout} from '../../features/community/components/Layout';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import type {NextPageWithLayout} from '../_app';

const QueryParameter = z.object({
  tag: z.string().optional(),
});
type QueryParameter = z.infer<typeof QueryParameter>;

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  selectedTagIdInitial: string;
  communityNickname: string;
  isSignedIn: boolean;
  isStudentVerificationRequired: boolean;
};

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const selectedTagIdInitial = context.validatedQuery.tag ?? '';
    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    // 大学認証未完了ユーザーは未ログイン相当の表示にするため、ニックネームも空にする
    const isSignedIn = isValid && !context.shouldTreatAsLoggedOut;

    if (!isSignedIn) {
      return {
        props: {
          selectedTagIdInitial,
          communityNickname: '',
          isSignedIn: false,
          isStudentVerificationRequired: context.isStudentVerificationRequired,
        },
      };
    }

    const usersMeResponse = await getUsersMe({cookies});
    if (!usersMeResponse.ok) {
      return {
        props: {
          selectedTagIdInitial,
          communityNickname: '',
          isSignedIn: false,
          isStudentVerificationRequired: context.isStudentVerificationRequired,
        },
      };
    }

    const props: Props = {
      selectedTagIdInitial,
      communityNickname: usersMeResponse.value.communityNickname,
      isSignedIn: true,
      isStudentVerificationRequired: context.isStudentVerificationRequired,
    };

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const CommunityQuestionList: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  selectedTagIdInitial,
  communityNickname,
  isSignedIn,
  isStudentVerificationRequired,
}) => {
  return (
    <>
      <CommunityQuestionListPage
        selectedTagIdInitial={selectedTagIdInitial}
        communityNickname={communityNickname}
        isSignedIn={isSignedIn}
        isStudentVerificationRequired={isStudentVerificationRequired}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

CommunityQuestionList.getLayout = (page) => (
  <CommunityLayout>{page}</CommunityLayout>
);

export default CommunityQuestionList;
